import styled from '@emotion/native';
import type { ColorName } from '@emotion/react';
import { Col } from 'components/base';
import { Dialog, Spinner, Text, Visible } from 'design-system';
import { MARGIN } from 'design-system/theme';

import React, { type PropsWithChildren, useCallback, useState } from 'react';
import { strings } from 'strings';

interface ConfirmationDialogProps extends PropsWithChildren {
  title: string;
  message: string;
  buttonText: string;
  status: ColorName;
  errorMessage?: string;
  reason?: React.FC;
  onSubmit: () => Promise<void>;
  setDialogState?: () => void;
}

type Step = 'initial' | 'loading' | 'error';

const SpinnerWrapper = styled(Col)({
  marginTop: MARGIN.m,
});

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  onSubmit,
  title,
  message,
  buttonText,
  status,
  errorMessage = 'An error has occurred, please try again',
  setDialogState,
  children,
}) => {
  const [isModalVisible, setVisible] = React.useState(true);
  const [formStep, setFormStep] = useState<Step>('initial');

  const reset = useCallback(() => {
    setVisible(false);

    setFormStep('initial');
    setDialogState && setDialogState();
  }, [setDialogState]);

  const handleSubmit = useCallback(async () => {
    setFormStep('loading');
    try {
      await onSubmit();
      reset();
    } catch (error) {
      setFormStep('error');
    }
  }, [onSubmit, reset]);

  const isLoading = formStep === 'loading';
  const isError = formStep === 'error';

  const notLoading = !isLoading;

  const primary = notLoading
    ? {
        onPress: handleSubmit,
        text: buttonText,
      }
    : undefined;

  const secondary = notLoading
    ? {
        onPress: reset,
        text: strings.ButtonTextCancel,
      }
    : undefined;

  return (
    <>
      <Dialog
        isOpen={isModalVisible}
        onRequestClose={reset}
        title={title}
        primary={primary}
        secondary={secondary}
        borderColor={status}
      >
        <Text>{message}</Text>

        <Visible if={children}>{children}</Visible>

        <Visible if={isLoading}>
          <SpinnerWrapper flex={1} alignItems="center" justifyContent="center">
            <Spinner />
          </SpinnerWrapper>
        </Visible>

        <Visible if={isError}>
          <Text color="status-critical">{errorMessage}</Text>
        </Visible>
      </Dialog>
    </>
  );
};
